import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function RegisterPage() {
  const { register, user, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", farm_name: "", region: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok) navigate("/portal", { replace: true });
    else setErr(res.error);
  };

  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="register-already-signed-in">
        <div className="bloom-card p-10 max-w-md text-center">
          <div className="bloom-label mb-3">Already signed in</div>
          <h2 className="font-serif text-3xl mb-3">You&rsquo;re signed in as {user.name}.</h2>
          <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-6">
            To register a new grower account you&rsquo;ll need to sign out first.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <button onClick={async () => { await logout(); }} className="bloom-btn bloom-btn-primary" data-testid="register-signout-first">
              Sign out, then register
            </button>
            <button onClick={() => navigate(user.role === "admin" ? "/admin" : "/portal")} className="bloom-btn bloom-btn-outline">
              Back to {user.role === "admin" ? "Console" : "Portal"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-stretch" data-testid="register-page">
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden bg-[var(--bloom-ink)] text-[var(--bloom-bg)] p-16 flex-col justify-between">
        <div className="relative z-10">
          <div className="bloom-seal" style={{background: "var(--bloom-bg)", color: "var(--bloom-ink)"}}>HBC<br/>2026</div>
          <div className="mt-12 max-w-md">
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-60 mb-6">✦ Begin Stage Two ✦</div>
            <h1 className="font-serif text-5xl leading-[1.05]">
              Twenty-five founding spots. <em>One</em> may be yours.
            </h1>
            <p className="mt-8 font-serif italic text-lg opacity-70 leading-relaxed">
              Your prospectus account tracks every chapter read, document signed,
              and acknowledgement made. A quiet record of your journey to the Bloom Club.
            </p>
          </div>
        </div>
        <div className="relative z-10 text-xs tracking-[0.2em] uppercase opacity-50">Heritage Dahlias · Aotearoa</div>
        <div className="bloom-grain" aria-hidden />
      </div>

      <div className="flex-1 flex items-center justify-center p-8 md:p-16 bg-[var(--bloom-bg)]">
        <div className="w-full max-w-sm bloom-rise">
          <div className="bloom-divider mb-10">✦ Create Account ✦</div>
          <h2 className="font-serif text-4xl mb-2">Begin your review</h2>
          <p className="text-sm text-[var(--bloom-ink-soft)] mb-10">A 4-minute setup. We will never share your details.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="register-form">
            <div>
              <label className="bloom-label block mb-1">Full name</label>
              <input required value={form.name} onChange={update("name")} className="bloom-input" data-testid="register-name-input" />
            </div>
            <div>
              <label className="bloom-label block mb-1">Farm or business name</label>
              <input value={form.farm_name} onChange={update("farm_name")} className="bloom-input" data-testid="register-farm-input" />
            </div>
            <div>
              <label className="bloom-label block mb-1">Region</label>
              <input value={form.region} onChange={update("region")} placeholder="e.g. Hawke&apos;s Bay" className="bloom-input" data-testid="register-region-input" />
            </div>
            <div>
              <label className="bloom-label block mb-1">Email</label>
              <input type="email" required value={form.email} onChange={update("email")} className="bloom-input" data-testid="register-email-input" />
            </div>
            <div>
              <label className="bloom-label block mb-1">Password (min 6 chars)</label>
              <input type="password" required minLength={6} value={form.password} onChange={update("password")} className="bloom-input" data-testid="register-password-input" />
            </div>

            {err && <div className="text-sm text-[var(--bloom-accent)] font-serif italic" data-testid="register-error">{err}</div>}

            <button type="submit" disabled={loading} className="bloom-btn bloom-btn-primary w-full" data-testid="register-submit-btn">
              {loading ? "Creating…" : "Create Account & Begin"}
            </button>
          </form>

          <div className="mt-8 text-sm text-[var(--bloom-ink-soft)] text-center">
            Already registered?{" "}
            <Link to="/login" className="underline decoration-[var(--bloom-brand)] underline-offset-4" data-testid="register-login-link">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
